/**
 * Copy Trading WebSocket Store (Zustand)
 *
 * 카피 트레이딩 실시간 데이터 관리
 * - Mock WebSocket 시뮬레이션 (5초마다 가격 업데이트)
 * - 실제 WebSocket 연결 지원
 * - 포지션 PnL 실시간 계산
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import { create } from 'zustand';
import { getBigNumber } from '../../../utils/getBigNumber';

import type {
  CopyPosition,
  CopyPositionUpdateEvent,
  CopyOrderFilledEvent,
  CopyPositionClosedEvent,
  CopyFollowAlertEvent,
} from '../../../types/copyTrading';

// Mock 데이터 제거 (Real WebSocket 사용)

/**
 * WebSocket Store State 인터페이스
 */
interface CopyWebsocketState {
  // ==========================================
  // State
  // ==========================================

  /** WebSocket 연결 상태 */
  isConnected: boolean;

  /** 실시간 포지션 목록 */
  positions: CopyPosition[];

  /** 알림 목록 */
  alerts: CopyFollowAlertEvent[];

  // ==========================================
  // Actions
  // ==========================================

  /** WebSocket 연결 시작 */
  connect: () => void;

  /** WebSocket 연결 해제 */
  disconnect: () => void;

  /** 포지션 가격 업데이트 (Mock) */
  updatePositionPrice: (positionId: number, newPrice: number) => void;

  /** 주문 체결 이벤트 핸들러 */
  handleOrderFilled: (event: CopyOrderFilledEvent) => void;

  /** 포지션 청산 이벤트 핸들러 */
  handlePositionClosed: (event: CopyPositionClosedEvent) => void;

  /** 알림 이벤트 핸들러 */
  handleAlert: (event: CopyFollowAlertEvent) => void;

  /** 알림 제거 */
  removeAlert: (followId: number) => void;
}

/**
 * Copy WebSocket Store
 */
export const useCopyWebsocketStore = create<CopyWebsocketState>((set, get) => ({
  // ==========================================
  // Initial State
  // ==========================================
  isConnected: false,
  positions: [],
  alerts: [],

  // ==========================================
  // Actions
  // ==========================================

  /**
   * WebSocket 연결 시작
   *
   * Mock Mode: 5초마다 랜덤 가격 변동 시뮬레이션
   * Real Mode: 실제 WebSocket 서버에 연결
   */
  connect: () => {
    const USE_MOCK_WS = import.meta.env.VITE_USE_MOCK_API === 'true';

    if (USE_MOCK_WS) {
      // ==========================================
      // Mock WebSocket 시뮬레이션
      // ==========================================
      console.log('[Copy WebSocket] Mock WebSocket 연결 시작');

      // Mock 포지션 데이터 초기화
      set({
        isConnected: true,
        positions: [], // Mock 제거 (Real WebSocket에서 받음)
      });

      // 5초마다 랜덤 포지션 가격 업데이트
      const interval = setInterval(() => {
        const positions = get().positions;

        if (positions.length === 0) {
          return;
        }

        // 랜덤 포지션 선택
        const randomIndex = Math.floor(Math.random() * positions.length);
        const randomPosition = positions[randomIndex];

        // 가격 변동 (-1% ~ +1%)
        const priceChangePercent = (Math.random() - 0.5) * 2;  // -1 ~ +1
        const priceChange = getBigNumber(randomPosition.currentPrice)
          .multipliedBy(priceChangePercent / 100)
          .toNumber();

        const newPrice = getBigNumber(randomPosition.currentPrice)
          .plus(priceChange)
          .toNumber();

        // 가격 업데이트
        get().updatePositionPrice(randomPosition.id, newPrice);

        console.log(
          `[Copy WebSocket] ${randomPosition.symbol} 가격 업데이트:`,
          randomPosition.currentPrice,
          '→',
          newPrice.toFixed(2),
          `(${priceChangePercent > 0 ? '+' : ''}${priceChangePercent.toFixed(2)}%)`
        );
      }, 5000);  // 5초마다

      // interval ID 저장 (disconnect 시 정리용)
      (window as any).__copyWsInterval = interval;
    } else {
      // ==========================================
      // Real WebSocket 연결 (나중에 구현)
      // ==========================================
      console.log('[Copy WebSocket] Real WebSocket 연결 시작');
      // TODO: 실제 WebSocket 서버 연결
      // const socket = io(import.meta.env.VITE_APP_SOCKET_URL);
      // socket.emit('subscribe', { channels: ['copy-positions', 'copy-orders'] });
      // socket.on('copy-position-update', get().handlePositionUpdate);

      set({ isConnected: true });
    }
  },

  /**
   * WebSocket 연결 해제
   */
  disconnect: () => {
    console.log('[Copy WebSocket] 연결 해제');

    // Mock interval 정리
    if ((window as any).__copyWsInterval) {
      clearInterval((window as any).__copyWsInterval);
      delete (window as any).__copyWsInterval;
    }

    // TODO: Real WebSocket 연결 해제
    // socket?.disconnect();

    set({
      isConnected: false,
      positions: [],
      alerts: [],
    });
  },

  /**
   * 포지션 가격 업데이트
   */
  updatePositionPrice: (positionId: number, newPrice: number) => {
    set((state) => ({
      positions: state.positions.map((pos) => {
        if (pos.id !== positionId) {
          return pos;
        }

        // PnL 재계산 (BigNumber.js 사용)
        const priceDiff = getBigNumber(newPrice).minus(pos.entryPrice);
        const multiplier = pos.side === 'SHORT' ? -1 : 1;

        const unrealizedPnl = priceDiff
          .multipliedBy(pos.quantity)
          .multipliedBy(pos.leverage)
          .multipliedBy(multiplier)
          .toNumber();

        // 청산가와의 거리 계산 (위험 감지)
        const distanceToLiquidation = getBigNumber(newPrice)
          .minus(pos.liquidationPrice)
          .dividedBy(newPrice)
          .multipliedBy(100)
          .abs()
          .toNumber();

        // 청산 위험 경고 (20% 이내)
        if (distanceToLiquidation < 20) {
          console.warn(
            `[Copy WebSocket] 청산 위험! ${pos.symbol} 청산가까지 ${distanceToLiquidation.toFixed(1)}%`
          );

          // 알림 추가
          get().handleAlert({
            followId: pos.followId,
            type: 'LIQUIDATION_RISK',
            message: `${pos.symbol} 포지션이 청산 위험 상태입니다 (청산가까지 ${distanceToLiquidation.toFixed(1)}%)`,
            severity: 'warning',
          });
        }

        return {
          ...pos,
          currentPrice: newPrice,
          unrealizedPnl,
          updatedAt: new Date().toISOString(),
        };
      }),
    }));
  },

  /**
   * 주문 체결 이벤트 핸들러
   */
  handleOrderFilled: (event: CopyOrderFilledEvent) => {
    console.log('[Copy WebSocket] 주문 체결:', event);
    // TODO: 새로운 포지션 추가 또는 기존 포지션 업데이트
  },

  /**
   * 포지션 청산 이벤트 핸들러
   */
  handlePositionClosed: (event: CopyPositionClosedEvent) => {
    console.log('[Copy WebSocket] 포지션 청산:', event);

    set((state) => ({
      positions: state.positions.filter((pos) => pos.id !== event.positionId),
    }));
  },

  /**
   * 알림 이벤트 핸들러
   */
  handleAlert: (event: CopyFollowAlertEvent) => {
    console.log('[Copy WebSocket] 알림:', event);

    // 중복 알림 방지 (같은 followId + type)
    const existingAlert = get().alerts.find(
      (alert) => alert.followId === event.followId && alert.type === event.type
    );

    if (!existingAlert) {
      set((state) => ({
        alerts: [...state.alerts, event],
      }));
    }
  },

  /**
   * 알림 제거
   */
  removeAlert: (followId: number) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.followId !== followId),
    }));
  },
}));

/**
 * 포지션 PnL 계산 헬퍼 (외부 사용)
 */
export const calculatePositionPnl = (
  entryPrice: number,
  currentPrice: number,
  quantity: number,
  leverage: number,
  side: 'LONG' | 'SHORT'
): number => {
  const priceDiff = getBigNumber(currentPrice).minus(entryPrice);
  const multiplier = side === 'SHORT' ? -1 : 1;

  return priceDiff
    .multipliedBy(quantity)
    .multipliedBy(leverage)
    .multipliedBy(multiplier)
    .toNumber();
};

/**
 * 청산가 계산 헬퍼
 */
export const calculateLiquidationPrice = (
  entryPrice: number,
  leverage: number,
  side: 'LONG' | 'SHORT'
): number => {
  const ratio = getBigNumber(1).dividedBy(leverage);

  if (side === 'LONG') {
    // LONG: liquidationPrice = entryPrice * (1 - 1/leverage)
    return getBigNumber(entryPrice)
      .multipliedBy(getBigNumber(1).minus(ratio))
      .toNumber();
  } else {
    // SHORT: liquidationPrice = entryPrice * (1 + 1/leverage)
    return getBigNumber(entryPrice)
      .multipliedBy(getBigNumber(1).plus(ratio))
      .toNumber();
  }
};
